import * as THREE from 'three'

export const IK_CHAINS = {
	mixamorigLeftHand: ['mixamorigLeftShoulder', 'mixamorigLeftArm', 'mixamorigLeftForeArm', 'mixamorigLeftHand'],
	mixamorigRightHand: ['mixamorigRightShoulder', 'mixamorigRightArm', 'mixamorigRightForeArm', 'mixamorigRightHand'],
	mixamorigLeftFoot: ['mixamorigLeftUpLeg', 'mixamorigLeftLeg', 'mixamorigLeftFoot'],
	mixamorigRightFoot: ['mixamorigRightUpLeg', 'mixamorigRightLeg', 'mixamorigRightFoot'],
	mixamorigHead: ['mixamorigSpine', 'mixamorigSpine1', 'mixamorigSpine2', 'mixamorigNeck', 'mixamorigHead'],
}

// Maps end-effector names to their mirrored counterpart
export const IK_MIRROR_MAP = {
	mixamorigLeftHand: 'mixamorigRightHand',
	mixamorigRightHand: 'mixamorigLeftHand',
	mixamorigLeftFoot: 'mixamorigRightFoot',
	mixamorigRightFoot: 'mixamorigLeftFoot',
}

// Rotation constraints per joint: [min, max] in radians per Euler axis
// Joints not listed here are unconstrained
const IK_CONSTRAINTS = {
	// Knees — hinge joints, bend backward only
	mixamorigLeftLeg:   { x: [-0.05, 2.6],  y: [-0.1, 0.1],   z: [-0.1, 0.1] },
	mixamorigRightLeg:  { x: [-0.05, 2.6],  y: [-0.1, 0.1],   z: [-0.1, 0.1] },

	// Elbows — hinge joints with forearm pronation/supination
	mixamorigLeftForeArm:  { x: [-0.1, 0.1],  y: [-2.6, 0.1],  z: [-0.1, 0.1] },
	mixamorigRightForeArm: { x: [-0.1, 0.1],  y: [-0.1, 2.6],  z: [-0.1, 0.1] },

	// Hips — ball-and-socket, wide but bounded
	mixamorigLeftUpLeg:  { x: [-2.0, 2.0],  y: [-1.0, 1.0],  z: [-0.8, 0.8] },
	mixamorigRightUpLeg: { x: [-2.0, 2.0],  y: [-1.0, 1.0],  z: [-0.8, 0.8] },

	// Shoulders — stabilizer joints, limited range
	mixamorigLeftShoulder:  { x: [-0.4, 0.4],  y: [-0.4, 0.4],  z: [-0.3, 0.5] },
	mixamorigRightShoulder: { x: [-0.4, 0.4],  y: [-0.4, 0.4],  z: [-0.5, 0.3] },

	// Upper arms — ball-and-socket, wide range
	mixamorigLeftArm:  { x: [-2.5, 2.5],  y: [-1.5, 1.5],  z: [-2.5, 1.0] },
	mixamorigRightArm: { x: [-2.5, 2.5],  y: [-1.5, 1.5],  z: [-1.0, 2.5] },

	// Spine — small rotation per segment
	mixamorigSpine:  { x: [-0.5, 0.8],  y: [-0.4, 0.4],  z: [-0.3, 0.3] },
	mixamorigSpine1: { x: [-0.5, 0.8],  y: [-0.4, 0.4],  z: [-0.3, 0.3] },
	mixamorigSpine2: { x: [-0.5, 0.8],  y: [-0.4, 0.4],  z: [-0.3, 0.3] },

	// Neck — moderate range
	mixamorigNeck: { x: [-0.8, 0.8],  y: [-1.0, 1.0],  z: [-0.5, 0.5] },
}

const _boneWorldPos = new THREE.Vector3()
const _endWorldPos = new THREE.Vector3()
const _toEnd = new THREE.Vector3()
const _toTarget = new THREE.Vector3()
const _boneWorldQuat = new THREE.Quaternion()
const _inverseQuat = new THREE.Quaternion()
const _rotationQuat = new THREE.Quaternion()
const _identityQuat = new THREE.Quaternion()
const _finalPos = new THREE.Vector3()
const _cross = new THREE.Vector3()
const _endWorldQuat = new THREE.Quaternion()
const _parentWorldQuat = new THREE.Quaternion()

// Give straight limbs a consistent bend direction when pulling inward.
const IK_HINGES = {
	mixamorigLeftLeg: { axis: 'x', direction: 1 },
	mixamorigRightLeg: { axis: 'x', direction: 1 },
	mixamorigLeftForeArm: { axis: 'y', direction: -1 },
	mixamorigRightForeArm: { axis: 'y', direction: 1 },
}

function clamp(value, min, max) {
	return Math.max(min, Math.min(max, value))
}

class IKSolver {
	constructor() {
		this.iterations = 64
		this.threshold = 0.1
	}

	applyConstraints(bone) {
		const constraint = IK_CONSTRAINTS[bone.name]
		if (!constraint) return

		bone.rotation.x = clamp(bone.rotation.x, constraint.x[0], constraint.x[1])
		bone.rotation.y = clamp(bone.rotation.y, constraint.y[0], constraint.y[1])
		bone.rotation.z = clamp(bone.rotation.z, constraint.z[0], constraint.z[1])
	}

	solve(bones, target) {
		if (!bones || bones.length < 2 || bones.some(bone => !bone)) return

		const endEffector = bones[bones.length - 1]
		endEffector.getWorldPosition(_finalPos)
		let bestDistance = _finalPos.distanceToSquared(target)
		if (bestDistance < this.threshold * this.threshold) return

		// Keep an existing pose if constraints cannot produce a closer solution.
		const bestPose = bones.map(bone => bone.quaternion.clone())
		// Wrists keep their bend and twist relative to the forearm.
		const preserveWorldOrientation = !endEffector.name.endsWith('Hand')
		if (preserveWorldOrientation) endEffector.getWorldQuaternion(_endWorldQuat).normalize()

		for (let j = 1; j < bones.length - 1; j++) {
			const bone = bones[j]
			const hinge = IK_HINGES[bone.name]
			if (!hinge || Math.abs(bone.rotation[hinge.axis]) > 0.01) continue
			bones[j - 1].getWorldPosition(_boneWorldPos)
			if (target.distanceToSquared(_boneWorldPos) < _finalPos.distanceToSquared(_boneWorldPos)) {
				bone.rotation[hinge.axis] = hinge.direction * 0.1
				bone.updateMatrixWorld(true)
			}
		}

		for (let i = 0; i < this.iterations; i++) {
			for (let j = bones.length - 2; j >= 0; j--) {
				const bone = bones[j]

				bone.getWorldPosition(_boneWorldPos)
				endEffector.getWorldPosition(_endWorldPos)

				_toEnd.copy(_endWorldPos).sub(_boneWorldPos)
				_toTarget.copy(target).sub(_boneWorldPos)

				if (_toEnd.lengthSq() < 0.0001 || _toTarget.lengthSq() < 0.0001) continue

				_toEnd.normalize()
				_toTarget.normalize()

				bone.getWorldQuaternion(_boneWorldQuat).normalize()
				_inverseQuat.copy(_boneWorldQuat).invert()

				_toEnd.applyQuaternion(_inverseQuat)
				_toTarget.applyQuaternion(_inverseQuat)

				const hinge = IK_HINGES[bone.name]
				if (hinge) {
					// Solve elbows and knees around their hinge, rather than
					// rotating freely and then discarding most of that rotation.
					_toEnd[hinge.axis] = 0
					_toTarget[hinge.axis] = 0
					if (_toEnd.lengthSq() < 0.0001 || _toTarget.lengthSq() < 0.0001) continue
					_toEnd.normalize()
					_toTarget.normalize()
					_cross.crossVectors(_toEnd, _toTarget)
					const angle = Math.atan2(_cross[hinge.axis], _toEnd.dot(_toTarget))
					bone.rotation[hinge.axis] += clamp(angle, -0.35, 0.35)
				} else {
					_rotationQuat.setFromUnitVectors(_toEnd, _toTarget)
					const angle = 2 * Math.acos(clamp(_rotationQuat.w, -1, 1))
					if (angle > 0.35) {
						_rotationQuat.slerp(_identityQuat, 1 - 0.35 / angle)
					}
					bone.quaternion.multiply(_rotationQuat)
				}

				this.applyConstraints(bone)

				bone.updateMatrixWorld(true)
			}

			endEffector.getWorldPosition(_finalPos)
			const distance = _finalPos.distanceToSquared(target)
			if (distance < bestDistance) {
				bestDistance = distance
				bones.forEach((bone, index) => bestPose[index].copy(bone.quaternion))
			}
			if (bestDistance < this.threshold * this.threshold) break
		}

		bones.forEach((bone, index) => bone.quaternion.copy(bestPose[index]))
		bones[0].updateMatrixWorld(true)
		if (preserveWorldOrientation) {
			// Keep ankles and head steady. Non-uniform limb scaling can make
			// matrix decomposition return a non-unit quaternion, so normalize it.
			endEffector.parent.getWorldQuaternion(_parentWorldQuat).normalize()
			endEffector.quaternion.copy(_parentWorldQuat.invert().multiply(_endWorldQuat)).normalize()
			endEffector.updateMatrixWorld(true)
		}
	}
}

export default IKSolver
